import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, ArrowRight, QrCode, RefreshCw } from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { supabase } from '../lib/supabase';
import { useEmail } from '../hooks/useEmail';

const SignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendPasswordReset } = useEmail();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQrLogin, setShowQrLogin] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(
    location.state?.message || null
  );
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { signIn } = useSupabase();

  // Generate random captcha
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(result);
    setCaptchaInput('');
    setCaptchaError('');
  };

  // Check if all fields are completed
  const isFormComplete = formData.email.trim() !== '' && formData.password.trim() !== '';

  // Check captcha settings from localStorage
  useEffect(() => {
    const adminSettings = localStorage.getItem('admin_settings');
    if (adminSettings) {
      try {
        const settings = JSON.parse(adminSettings);
        setCaptchaEnabled(settings.enableCaptcha && settings.captchaOnLogin);
      } catch (err) {
        console.error('Error parsing admin settings:', err);
        setCaptchaEnabled(false);
      }
    } else {
      setCaptchaEnabled(false);
    }
  }, []);

  // Initialize captcha when form becomes complete
  React.useEffect(() => {
    if (isFormComplete && !captchaCode && captchaEnabled) {
      generateCaptcha();
    }
  }, [isFormComplete, captchaCode, captchaEnabled]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isFormComplete && captchaEnabled) {
      if (captchaInput !== captchaCode) {
        setCaptchaError('Captcha code is incorrect. Please try again.');
        generateCaptcha();
        return;
      }
    }
    
    setIsLoading(true);
    setLoginError('');
    
    try {
      // Sign in with Supabase
      const authResponse = await signIn(formData.email, formData.password);
      
      if (authResponse.error || !authResponse.user) {
        throw authResponse.error || new Error('Authentication failed');
      }
      
      // First try to get account type from user metadata
      let accountType = authResponse.user?.user_metadata?.account_type;
      
      // If not found in metadata, try to get from users table
      if (!accountType) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('account_type')
          .eq('email', formData.email)
          .maybeSingle();
        
        if (userError) {
          console.error('Error fetching user data:', userError);
          // Default to buyer if we can't determine account type
          accountType = 'buyer';
        } else if (userData) {
          accountType = userData.account_type;
        } else {
          // No user record found, default to buyer
          accountType = 'buyer';
        }
      }
      
      // Redirect based on account type
      switch (accountType) {
        case 'buyer':
          navigate('/profile');
          break;
        case 'supplier':
          navigate('/seller-central');
          break;
        case 'admin':
          navigate('/admin');
          break;
        default:
          navigate('/');
          break;
      }
    } catch (error) {
      console.error('Error signing in:', error);
      setLoginError('Invalid email or password. Please try again.');
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setCaptchaError('');
    setLoginError('');
  };
  
  // Handle forgot password
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      setLoginError('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    setLoginError('');
    
    try {
      // Request password reset from Supabase
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: `${window.location.origin}/signin?reset=true`,
      });
      
      if (error) throw error;
      
      // Send custom password reset email
      const resetLink = `${window.location.origin}/reset-password?email=${encodeURIComponent(forgotPasswordEmail)}`;
      await sendPasswordReset(forgotPasswordEmail, 'User', resetLink);
      
      setResetEmailSent(true);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setLoginError('Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleQrLogin = () => {
    setShowQrLogin(!showQrLogin);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <img 
            src="/madeinbd-logo.png" 
            alt="Made in BD" 
            className="h-12 w-auto object-contain"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Sign in to your account
        </h2>
        {successMessage && (
          <div className="mt-2 p-2 bg-green-50 text-green-800 rounded-md text-center">
            {successMessage}
          </div>
        )}
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link to="/create-account" className="font-medium text-orange-600 hover:text-orange-500">
            create a new account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Toggle between QR and Email login */}
          <div className="flex justify-center mb-6">
            <div className="flex p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setShowQrLogin(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  !showQrLogin 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Email & Password
              </button>
              <button
                onClick={() => setShowQrLogin(true)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                  showQrLogin 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <QrCode className="h-4 w-4 mr-2" />
                QR Code
              </button>
            </div>
          </div>

          {forgotPasswordMode ? (
            <form className="space-y-6" onSubmit={handleForgotPassword}>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reset Your Password</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>
              
              {/* Email */}
              <div>
                <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-700">
                  Email address *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="forgotPasswordEmail"
                    name="forgotPasswordEmail"
                    type="email"
                    autoComplete="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>
              
              {/* Error Message */}
              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {loginError}
                </div>
              )}
              
              {/* Success Message */}
              {resetEmailSent && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  Password reset link has been sent to your email address.
                </div>
              )}
              
              {/* Submit Button */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setForgotPasswordMode(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Back to Login
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !forgotPasswordEmail}
                  className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span>Send Reset Link</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          ) : showQrLogin ? (
            <div className="text-center">
              <div className="mb-4">
                <div className="bg-white p-2 border border-gray-300 rounded-lg inline-block">
                  <div className="bg-gray-100 p-4 rounded">
                    <QrCode className="h-48 w-48 mx-auto text-gray-800" />
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Scan with Mobile App</h3>
              <p className="text-sm text-gray-600 mb-6">
                Open the Made in BD app on your phone and scan this QR code to sign in instantly.
              </p>
              <div className="flex justify-center space-x-4">
                <a href="#" className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
                  </svg>
                  Android App
                </a>
                <a href="#" className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.52 1.49-1.14 2.95-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.26 2.01-1.56 4.09-3.74 4.25z" />
                  </svg>
                  iOS App
                </a>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Enter your password"
                  />
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {loginError}
                </div>
              )}

              {/* Captcha - Only show when form is complete */}
              {isFormComplete && captchaEnabled && (
                <div className="animate-fade-in">
                  <label htmlFor="captcha" className="block text-sm font-medium text-gray-700">
                    Security Verification *
                  </label>
                  <div className="mt-1 space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gray-100 px-4 py-2 rounded-md border border-gray-300 font-mono text-lg tracking-wider select-none">
                        {captchaCode}
                      </div>
                      <button
                        type="button"
                        onClick={generateCaptcha}
                        className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                        title="Refresh captcha"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </div>
                    <input
                      id="captcha"
                      name="captcha"
                      type="text"
                      required
                      value={captchaInput}
                      onChange={(e) => {
                        setCaptchaInput(e.target.value);
                        setCaptchaError('');
                      }}
                      className={`appearance-none block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm ${
                        captchaError ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter the code above"
                    />
                    {captchaError && (
                      <p className="text-sm text-red-600">{captchaError}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <button 
                    type="button"
                    onClick={() => setForgotPasswordMode(true)}
                    className="font-medium text-orange-600 hover:text-orange-500 bg-transparent border-none p-0 cursor-pointer"
                  >
                    Forgot your password?
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading || !isFormComplete || (isFormComplete && captchaEnabled && captchaInput !== captchaCode)}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Sign in
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Social Login */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="ml-2">Google</span>
              </button>

              <button className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <span className="ml-2">WhatsApp</span>
              </button>
            </div>
          </div>

          {/* Business Account */}
          <div className="mt-6 p-4 bg-orange-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Business Account?</h3>
            <p className="text-sm text-gray-600 mb-3">
              Join as a supplier and start selling your products to global buyers.
            </p>
            <Link 
              to="/suppliers" 
              className="text-sm font-medium text-orange-600 hover:text-orange-500"
            >
              Learn more about becoming a supplier →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;